// Limites de depósito Pix vindos do backend (GET /api/deposits/pix/limits).
// O front não define nenhum valor: mudar uma regra no banco não exige deploy aqui.

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const formatAmount = (value) => currencyFormatter.format(Number(value) || 0);

// O backend serializa valores monetários como string para não perder precisão.
export const parseAmount = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

// getAllowedRange devolve a faixa que o campo de valor deve aceitar. Na primeira compra
// first_purchase_amount é um teto, não um valor obrigatório: o mínimo geral continua valendo.
export const getAllowedRange = (limits) => {
  const firstPurchaseAmount = limits?.firstPurchase ? parseAmount(limits?.firstPurchaseAmount) : null;
  const maxAvailable = parseAmount(limits?.maxAmountAvailable);
  const generalMax = maxAvailable !== null ? maxAvailable : parseAmount(limits?.maxAmountPerTx);

  const max =
    firstPurchaseAmount !== null
      ? generalMax !== null
        ? Math.min(firstPurchaseAmount, generalMax)
        : firstPurchaseAmount
      : generalMax;

  return {
    min: parseAmount(limits?.minAmount),
    max,
    firstPurchaseAmount,
  };
};

// describeLimits é o texto de apoio exibido abaixo do campo de valor.
export const describeLimits = (limits) => {
  if (!limits) return '';
  if (limits.blocked) return limits.blockedMessage || '';

  const { min, max, firstPurchaseAmount } = getAllowedRange(limits);

  if (firstPurchaseAmount !== null) {
    return `Seu primeiro depósito pode ser de até R$ ${formatAmount(max)}.`;
  }

  if (min !== null && max !== null) {
    return `Valor mínimo/máximo: R$ ${formatAmount(min)} / R$ ${formatAmount(max)}`;
  }
  if (min !== null) {
    return `Valor mínimo: R$ ${formatAmount(min)}`;
  }
  if (max !== null) {
    return `Valor máximo: R$ ${formatAmount(max)}`;
  }

  return '';
};

// describeAvailableToday é o saldo restante do período, exibido abaixo do campo de valor.
//
// A janela de onboarding e o teto diário correm ao mesmo tempo, com valores diferentes: nas
// primeiras horas depois da primeira compra quem manda é a janela, bem menor que o teto do dia.
// Mostrar sempre o diário anunciaria um saldo que o backend recusaria, então vale o que estiver
// mais apertado.
export const describeAvailableToday = (limits) => {
  const periodos = [limits?.firstPeriod?.open ? limits.firstPeriod : null, limits?.daily]
    .map((periodo) => ({
      remaining: parseAmount(periodo?.remaining),
      limit: parseAmount(periodo?.limit),
    }))
    .filter((periodo) => periodo.remaining !== null && periodo.limit !== null);

  if (periodos.length === 0) return '';

  const menor = periodos.reduce((atual, outro) =>
    outro.remaining < atual.remaining ? outro : atual
  );

  return `Disponível hoje: R$ ${formatAmount(menor.remaining)} de R$ ${formatAmount(menor.limit)}.`;
};

// validateAmount dá retorno imediato com as regras que o backend informou. Não substitui a
// checagem do servidor, que continua sendo a autoridade.
export const validateAmount = (amount, limits) => {
  if (!Number.isFinite(amount) || amount <= 0) {
    return 'Informe o valor do depósito.';
  }

  if (!limits) return '';
  if (limits.blocked) {
    return limits.blockedMessage || 'Depósitos via Pix estão indisponíveis no momento.';
  }

  const { min, max, firstPurchaseAmount } = getAllowedRange(limits);

  if (min !== null && amount < min) {
    return `O valor mínimo para depósito é de R$ ${formatAmount(min)}.`;
  }
  if (max !== null && amount > max) {
    return firstPurchaseAmount !== null
      ? `Seu primeiro depósito pode ser de até R$ ${formatAmount(max)}.`
      : `No momento você pode depositar no máximo R$ ${formatAmount(max)}.`;
  }

  return '';
};

// describeRefusal junta a mensagem do backend com o horário de liberação, convertido para o fuso
// de quem está na tela.
export const describeRefusal = (message, retryAfter) => {
  const texto = message || 'Depósitos via Pix estão indisponíveis no momento.';
  if (!retryAfter) return texto;

  const liberacao = new Date(retryAfter);
  if (Number.isNaN(liberacao.getTime())) return texto;

  return `${texto} Novos depósitos são liberados a partir de ${liberacao.toLocaleString('pt-BR')}.`;
};

// getBlockedReason é a mensagem do bloqueio atual, quando existe, já com o horário de
// liberação quando o backend informa um.
export const getBlockedReason = (limits) => {
  if (!limits?.blocked) return '';

  return describeRefusal(limits.blockedMessage, limits.retryAfter);
};

// getUpgradePath é o convite para verificar a conta. Só aparece quando subir de nível resolve o
// bloqueio atual: oferecer sem resolver deixaria o usuário barrado depois do KYC.
export const getUpgradePath = (limits, refusalCode) => {
  const upgrade = limits?.upgrade;
  if (!upgrade) return '';

  const convite = `Verifique sua conta (Nível ${upgrade.level}) para depositar valores maiores.`;

  const tetoMaior = (atual, doProximoNivel) => {
    const aqui = parseAmount(atual);
    const noProximo = parseAmount(doProximoNivel);
    return noProximo !== null && (aqui === null || noProximo > aqui);
  };

  switch (refusalCode) {
    case 'AMOUNT_ABOVE_MAX_PER_TRANSACTION':
      return tetoMaior(limits.maxAmountPerTx, upgrade.maxAmountPerTx) ? convite : '';
    case 'FIRST_PURCHASE_ABOVE_MAX_AMOUNT':
      return tetoMaior(limits.firstPurchaseAmount, upgrade.firstPurchaseAmount) ? convite : '';
    case 'DAILY_LIMIT_EXCEEDED':
      return tetoMaior(limits.daily?.limit, upgrade.dailyLimit) ? convite : '';
    case 'AMOUNT_BELOW_MINIMUM': {
      const aqui = parseAmount(limits.minAmount);
      const noProximo = parseAmount(upgrade.minAmount);
      return noProximo !== null && aqui !== null && noProximo < aqui
        ? `Verifique sua conta (Nível ${upgrade.level}) para depositar valores menores.`
        : '';
    }
    default:
      return limits.blocked && upgrade.solvesCurrentBlock ? convite : '';
  }
};
